import React from 'react';

import type { DiscardPayload } from '../discard-confirmation-modal/discard-confirmation-modal';
import { defaultPromptsConfiguration } from '../prompts';
import { isOfType } from '../ts-utils';
import type {
  AiPluginEditorConfig,
  InitialPromptOptions,
  PromptsConfiguration,
  ReferenceElement,
  ResultsActionsCollection,
} from '../types';
import { useSessionStorage } from '../utils/use-session-storage';
import { useAiModalActions } from './slice/use-ai-modal-actions';
import { useAiModalReducer } from './slice/use-ai-modal-reducer';
import { useAiModalSelectors } from './slice/use-ai-modal-selectors';
import type { TextualPromptInputAreaRef } from './textual-prompt-input-area/textual-prompt-input-area';
import { useAiModalShortcuts } from './use-ai-modal-shortcuts';

const DISCARD_MODAL_STORAGE_KEY = 'ricos-ai-discard-modal';

type UseAiModalProps = {
  selection: string;
  closeModal: () => void;
  openDiscardModal: (referenceElement: ReferenceElement | null) => Promise<DiscardPayload>;
  modalRef: React.RefObject<any | null>;
  listRef: React.RefObject<any | null>;
  inputRef: React.RefObject<TextualPromptInputAreaRef | null>;
  publishModalAction: (action: string, value?: string) => void;
  initialPromptOptions?: InitialPromptOptions;
  contextualResultActions?: ResultsActionsCollection;
} & AiPluginEditorConfig;

export const useAiModalApi = ({
  selection,
  configurePrompts,
  getGatewayBaseUrl,
  appDefId,
  closeModal,
  openDiscardModal,
  modalRef,
  inputRef,
  listRef,
  publishModalAction,
  initialPromptOptions,
}: UseAiModalProps) => {
  const [dontShowDiscardModal, setDontShowDiscardModal] = useSessionStorage(
    DISCARD_MODAL_STORAGE_KEY,
    false
  );
  const [isDiscardModalOpen, setIsDiscardModalOpen] = React.useState(false);
  const [prompts] = React.useState<PromptsConfiguration>(
    configurePrompts ? configurePrompts(defaultPromptsConfiguration) : defaultPromptsConfiguration
  );
  const { state, dispatch } = useAiModalReducer(initialPromptOptions);
  const actions = useAiModalActions({
    state,
    dispatch,
    getGatewayBaseUrl,
    appDefId,
    inputRef,
    listRef,
    selection,
    initialPromptOptions,
  });

  const tryCloseModal = React.useCallback(async () => {
    if (isOfType(['INITIAL'], state) || dontShowDiscardModal) {
      closeModal();
      return;
    }

    setIsDiscardModalOpen(true);
    const discardResult = await openDiscardModal(inputRef.current);
    setIsDiscardModalOpen(false);

    if (discardResult.confirmed) {
      if (discardResult.dontShowAgain) {
        setDontShowDiscardModal(true);
      }
      closeModal();
      return;
    }
  }, [state.type, closeModal, openDiscardModal]);

  useAiModalShortcuts({
    state,
    selection,
    modalRef,
    closeModal,
    prompts,
    cancelPrompt: actions.cancelPrompt,
    triggerFreePrompt: actions.triggerFreePrompt,
    submitPrompt: actions.submitPrompt,
    setSelectedPromptIndex: actions.setSelectedPromptIndex,
    publishModalAction,
  });

  const { getResultActions, textualPrompt, promptsList } = useAiModalSelectors({
    publishModalAction,
    selection,
    prompts,
    state,
    closeModal,
    cancelPrompt: actions.cancelPrompt,
    submitPrompt: actions.submitPrompt,
    contextualResultActions: initialPromptOptions?.contextualResultActions,
  });

  return {
    ...actions,
    tryCloseModal,
    // Derived
    getResultActions,
    textualPrompt,
    promptsList,
    // State
    state,
    isDiscardModalOpen,
  };
};
